import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { authApi } from '../api/phase12Client';
import { clearSession, getStoredUser, getToken, storeSession, type AuthUser } from '../api/http';

interface AuthValue {
  ready: boolean;
  authRequired: boolean;
  user: AuthUser | null;
  token: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [authRequired, setAuthRequired] = useState(true);
  const [token, setToken] = useState<string | null>(getToken());
  const [user, setUser] = useState<AuthUser | null>(getStoredUser());

  useEffect(() => {
    authApi.status()
      .then((s) => setAuthRequired(s.auth_required))
      .catch(() => setAuthRequired(true))
      .finally(() => setReady(true));
    const onExpired = () => { setToken(null); setUser(null); };
    window.addEventListener('pm-auth-expired', onExpired);
    return () => window.removeEventListener('pm-auth-expired', onExpired);
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const r = await authApi.login(username, password);
    storeSession(r.token, r.user);
    setToken(r.token);
    setUser(r.user);
  }, []);

  const logout = useCallback(() => { clearSession(); setToken(null); setUser(null); }, []);

  const value = useMemo(() => ({ ready, authRequired, user, token, login, logout }), [ready, authRequired, user, token, login, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
